/**
 * Scene Library view (spec section 31).
 *
 * Each card answers the only two questions that matter:
 *   "can I use this right now?" and "what do I do if I cannot?"
 */

import { h } from '../../utils/dom.js';
import { formatDuration } from '../../utils/format.js';
import { LOOP_QUALITIES } from '../../data/scenes.js';
import { resolveRenderer, listProceduralScenes } from '../../scenes/procedural/index.js';
import { createScenePreview } from '../components/scene-preview.js';
import { card, button, note, segmented, sectionHeading, chipRow } from '../components/common.js';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'ready', label: 'Ready' },
  { id: 'pending', label: 'Needs asset' },
  { id: 'disabled', label: 'Disabled' },
];

export function renderScenes(ctx) {
  const { store, actions, sceneStore } = ctx;
  const state = store.get();
  const root = h('div', { class: 'stack-lg' });
  const filter = state.sceneFilter ?? 'all';
  const query = (state.sceneQuery ?? '').trim().toLowerCase();

  const all = sceneStore.all();
  const readyCount = all.filter((s) => isReady(s)).length;

  root.append(
    sectionHeading('Scene Library', `${all.length} scenes \u00b7 ${readyCount} ready to render`, button('Add scene', {
      kind: 'ghost',
      small: true,
      onClick: () => openSceneEditor(ctx, null),
    })),
  );

  root.append(
    note(
      'A scene is a reusable visual asset, not an episode. Generate it once, then reuse it across many BEX CAST episodes.',
      'info',
    ),
  );

  root.append(
    segmented(FILTERS, filter, (id) => {
      store.set({ sceneFilter: id });
      actions.render();
    }),
  );

  root.append(
    h('input', {
      type: 'search',
      placeholder: 'Filter by name, tag or mood',
      value: state.sceneQuery ?? '',
      'aria-label': 'Filter scenes',
      oninput: (event) => {
        store.set({ sceneQuery: event.target.value });
        clearTimeout(root.__t);
        root.__t = setTimeout(() => actions.render(), 220);
      },
    }),
  );

  const filtered = all.filter((scene) => {
    if (filter === 'ready' && !isReady(scene)) return false;
    if (filter === 'pending' && isReady(scene)) return false;
    if (filter === 'disabled' && scene.enabled !== false) return false;
    if (!query) return true;
    const haystack = [scene.name, scene.id, ...scene.tags, ...scene.moods, scene.description].join(' ').toLowerCase();
    return haystack.includes(query);
  });

  if (!filtered.length) {
    root.append(note('No scenes match this filter.', 'info'));
    return root;
  }

  const grid = h('div', { class: 'grid' });
  for (const scene of filtered) {
    grid.append(sceneCard(ctx, scene));
  }
  root.append(grid);

  root.append(
    card(
      [
        note(
          'Procedural scenes are drawn live by the Canvas engine during export. They need no download, no API key and no storage.',
          'info',
        ),
        h('div', { class: 'chip-row' }, listProceduralScenes().map((item) => h('span', { class: 'chip chip--accent' }, [item.label]))),
      ],
      { title: 'Built-in procedural scenes' },
    ),
  );

  return root;
}

function isReady(scene) {
  if (scene.enabled === false) return false;
  if (scene.assetType === 'procedural') return Boolean(resolveRenderer(scene).renderer);
  if (scene.assetType === 'video') return Boolean(scene.assetUrl);
  return false;
}

function sceneCard(ctx, scene) {
  const { actions, store, settings } = ctx;
  const selected = store.get().selectedScene?.id === scene.id;
  const ready = isReady(scene);
  const { renderer } = resolveRenderer(scene, { allowFallback: false });

  const preview = createScenePreview(scene, {
    width: 200,
    height: 356,
    animated: true,
    strength: settings.audioReactive ? settings.audioReactiveStrength / 100 : 0,
  });
  // Without this, every re-render would leak one animation loop per card.
  ctx.onDispose(() => preview.dispose());

  const badges = h('div', { class: 'scene-card__badges' }, [
    ready
      ? h('span', { class: 'chip chip--ok' }, ['ready'])
      : h('span', { class: 'chip chip--warn' }, ['needs asset']),
    scene.enabled === false ? h('span', { class: 'chip chip--danger' }, ['disabled']) : null,
    scene.assetType === 'procedural' ? h('span', { class: 'chip chip--accent' }, ['procedural']) : null,
  ]);

  const el = h(
    'article',
    { class: `scene-card${selected ? ' is-selected' : ''}${scene.enabled === false ? ' is-disabled' : ''}` },
    [
      h('div', { style: { position: 'relative' } }, [preview.element, badges]),
      h('div', { class: 'scene-card__body' }, [
        h('div', { class: 'scene-card__name' }, [scene.name]),
        h('div', { class: 'tiny dim' }, [
          `${formatDuration(scene.duration)} \u00b7 loop ${scene.loopQuality} \u00b7 used ${scene.usageCount}\u00d7`,
        ]),
        h('div', { class: 'tiny dim' }, [
          scene.provider ? `asset: ${scene.provider}` : scene.assetType === 'procedural' ? 'asset: procedural' : 'asset: none yet',
        ]),
        chipRow(scene.moods.slice(0, 2).map((mood) => h('span', { class: 'chip' }, [mood]))),
        h('div', { class: 'scene-card__actions' }, [
          button('Use', {
            kind: selected ? 'ghost' : 'primary',
            small: true,
            onClick: () => {
              actions.selectSceneById(scene.id);
              actions.setView('studio');
            },
          }),
          ready
            ? button('Preview', { kind: 'ghost', small: true, onClick: () => actions.selectSceneById(scene.id) })
            : button('Generate', { kind: 'primary', small: true, onClick: () => actions.openVidsForScene(scene.id) }),
          button('Import', {
            kind: 'ghost',
            small: true,
            onClick: () => actions.triggerImport(scene.id, 'local-library'),
          }),
          button('Edit', { kind: 'ghost', small: true, onClick: () => openSceneEditor(ctx, scene) }),
          button(scene.enabled === false ? 'Enable' : 'Disable', {
            kind: 'ghost',
            small: true,
            onClick: () => {
              const updated = ctx.sceneStore.setEnabled(scene.id, scene.enabled === false);
              ctx.actions.persistScene(updated);
              ctx.store.set({ library: ctx.sceneStore.all() });
              ctx.actions.render();
            },
          }),
        ]),
        scene.assetId
          ? button('Remove asset', {
              kind: 'danger',
              small: true,
              onClick: () => actions.deleteSceneAsset(scene.id),
            })
          : null,
      ]),
    ],
  );

  queueMicrotask(() => preview.start());
  return el;
}

/** Create / edit a scene definition. */
export async function openSceneEditor(ctx, scene) {
  const { actions, sceneStore } = ctx;
  const isNew = !scene;
  const draft = scene ?? {
    id: '',
    name: '',
    description: '',
    tags: [],
    moods: ['TECH'],
    environments: ['indoor'],
    cameraStyle: 'static',
    visualStyle: 'cinematic-realistic',
    duration: 10,
    loopable: true,
    loopQuality: 'unknown',
    assetType: 'none',
    proceduralId: null,
    promptIds: [],
    audioEffect: 'none',
    enabled: true,
    weight: 6,
    usageCount: 0,
    lastUsed: null,
  };

  const form = h('div', { class: 'stack' }, [
    h('div', { class: 'field' }, [
      h('label', { class: 'field__label', for: 'scene-name' }, ['Name']),
      h('input', { id: 'scene-name', type: 'text', value: draft.name, placeholder: 'Taxi Night' }),
    ]),
    h('div', { class: 'field' }, [
      h('label', { class: 'field__label', for: 'scene-id' }, ['Id (used for filenames)']),
      h('input', { id: 'scene-id', type: 'text', value: draft.id, placeholder: 'taxi-night', disabled: !isNew }),
    ]),
    h('div', { class: 'field' }, [
      h('label', { class: 'field__label', for: 'scene-desc' }, ['Description (feeds the prompt generator)']),
      h('textarea', { id: 'scene-desc', value: draft.description, placeholder: 'Interior of a moving taxi at night...' }),
    ]),
    h('div', { class: 'field' }, [
      h('label', { class: 'field__label', for: 'scene-tags' }, ['Tags (comma separated)']),
      h('input', { id: 'scene-tags', type: 'text', value: draft.tags.join(', ') }),
    ]),
    h('div', { class: 'field' }, [
      h('label', { class: 'field__label', for: 'scene-moods' }, ['Moods (comma separated)']),
      h('input', { id: 'scene-moods', type: 'text', value: draft.moods.join(', ') }),
    ]),
    h('div', { class: 'row' }, [
      h('div', { class: 'field', style: { flex: '1' } }, [
        h('label', { class: 'field__label', for: 'scene-duration' }, ['Loop length (s)']),
        h('input', { id: 'scene-duration', type: 'number', min: '2', max: '60', value: String(draft.duration) }),
      ]),
      h('div', { class: 'field', style: { flex: '1' } }, [
        h('label', { class: 'field__label', for: 'scene-weight' }, ['Weight (1-10)']),
        h('input', { id: 'scene-weight', type: 'number', min: '1', max: '10', value: String(draft.weight) }),
      ]),
    ]),
    h('div', { class: 'field' }, [
      h('label', { class: 'field__label', for: 'scene-loop' }, ['Loop quality']),
      h(
        'select',
        { id: 'scene-loop' },
        LOOP_QUALITIES.map((quality) =>
          h('option', { value: quality, selected: draft.loopQuality === quality }, [quality]),
        ),
      ),
    ]),
    h('div', { class: 'field' }, [
      h('label', { class: 'field__label', for: 'scene-procedural' }, ['Procedural renderer (optional)']),
      h(
        'select',
        { id: 'scene-procedural' },
        [
          h('option', { value: '', selected: !draft.proceduralId }, ['none']),
          ...listProceduralScenes().map((item) =>
            h('option', { value: item.id, selected: draft.proceduralId === item.id }, [item.label]),
          ),
        ],
      ),
    ]),
    isNew ? null : button('Duplicate this scene', { kind: 'ghost', small: true, onClick: () => {} }),
  ]);

  const saved = await ctx.actions.openSheet({
    title: isNew ? 'Add scene' : `Edit ${scene.name}`,
    body: form,
    actions: [
      { label: 'Cancel', value: 'cancel', kind: 'ghost' },
      ...(isNew ? [] : [{ label: 'Delete', value: 'delete', kind: 'danger' }]),
      { label: isNew ? 'Create' : 'Save', value: 'save', kind: 'primary' },
    ],
  });

  if (!saved || saved === 'cancel') return null;

  if (saved === 'delete') {
    sceneStore.remove(scene.id);
    store_setAndRender(ctx);
    return null;
  }

  const payload = {
    id: form.querySelector('#scene-id').value.trim() || slugifyName(form.querySelector('#scene-name').value),
    name: form.querySelector('#scene-name').value.trim() || 'Untitled scene',
    description: form.querySelector('#scene-desc').value.trim(),
    tags: splitList(form.querySelector('#scene-tags').value),
    moods: splitList(form.querySelector('#scene-moods').value).map((m) => m.toUpperCase()),
    environments: draft.environments,
    cameraStyle: draft.cameraStyle,
    visualStyle: draft.visualStyle,
    duration: Number(form.querySelector('#scene-duration').value) || 10,
    loopable: true,
    loopQuality: form.querySelector('#scene-loop').value,
    assetType: draft.assetType,
    proceduralId: form.querySelector('#scene-procedural').value || null,
    promptIds: draft.promptIds ?? [],
    audioEffect: draft.audioEffect ?? 'none',
    enabled: true,
    weight: Number(form.querySelector('#scene-weight').value) || 6,
    usageCount: draft.usageCount ?? 0,
    lastUsed: draft.lastUsed ?? null,
  };

  if (payload.proceduralId && payload.assetType === 'none') payload.assetType = 'procedural';

  if (isNew) {
    const created = sceneStore.add(payload);
    actions.persistScene(created);
  } else {
    const updated = sceneStore.update(scene.id, payload);
    actions.persistScene(updated);
  }

  await ctx.actions.loadLibrary();
  store_setAndRender(ctx);
  return payload;
}

function store_setAndRender(ctx) {
  ctx.store.set({ library: ctx.sceneStore.all() });
  ctx.actions.render();
}

function splitList(value) {
  return String(value ?? '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function slugifyName(value) {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}
